import { SearchOutlined, FileExcelOutlined } from '@ant-design/icons';
import * as XLSX from 'xlsx';
import {
  Breadcrumb,
  Button,
  Col,
  Input,
  Row,
  Space,
  Table,
  DatePicker,
  Tooltip,
  Dropdown,
  Menu,
  Checkbox,
  Spin,
  message,
} from 'antd';
import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ReactComponent as Back } from '../../../Images/Back.svg';
import { ReactComponent as ColumnList } from '../../../Images/ColumnList.svg';
import { ReactComponent as FilterIcon } from '../../../Images/FilterIcon.svg';
import { ReactComponent as PrintIcon } from '../../../Images/PrintIcon.svg';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { useDispatch, useSelector } from 'react-redux';
import { fetchInwardPackingDateWiseReport } from '../../../slices/report';
import eventBus from '../../../utils/eventBus';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import logo from '../../../Images/alux-logo.png';

dayjs.extend(customParseFormat);
const { RangePicker } = DatePicker;

function PackingReport() {
  let navigate = useNavigate();
  const dispatch = useDispatch();
  const { user: currentUser } = useSelector(state => state.auth);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pagesize, setPageSize] = useState(10);
  const [orderBy, setOrderBy] = useState('');
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const {
    PackingDateWisedata = [],
    totalRecord,
    status,
  } = useSelector(state => state.report);
  const [dateRange, setDateRange] = useState([
    dayjs().startOf('month'),
    dayjs(),
  ]);

  const handleSearch = e => {
    setSearch(e.target.value);
    setPage(1);
  };

  const handleDateRangeChange = dates => {
    if (dates) {
      setDateRange(dates);
    }
  };

  const fetchPackingDateWiseReportData = useCallback(() => {
    setLoading(true);
      const params = {
        page,
        pagesize,
        ...(search && { search }),
        ...(orderBy && { ordering: orderBy }),
      };
      if (dateRange && dateRange[0] && dateRange[1]) {
        params.from_date = dateRange[0].format('YYYY-MM-DD');
        params.to_date = dateRange[1].format('YYYY-MM-DD');
      }
      try {
         dispatch(fetchInwardPackingDateWiseReport(params)).unwrap();
      } catch (error) {
        console.error('API fetch error:', error);
      } finally {
        setLoading(false);
      }
  }, [dispatch, page, pagesize, search, orderBy, dateRange]);


  useEffect(() => {
    fetchPackingDateWiseReportData();
  }, [fetchPackingDateWiseReportData]);

  useEffect(() => {
    const handleReload = () => {
      fetchPackingDateWiseReportData();
    };

    eventBus.on("financialYear:refresh", handleReload);

    return () => {
      eventBus.off("financialYear:refresh", handleReload);
    };
  }, [fetchPackingDateWiseReportData]);

  const resetFilter = () => {
    setSearch('');
    setPage(1);
    setOrderBy('');
  };

  const handlePrintDownloadAll = async () => {
    setLoading(true);
    try {
      const params = {
        page: 1,
        pagesize: 10000,
        ...(search && { search }),
        ...(orderBy && { ordering: orderBy }),
        ...(dateRange && dateRange[0] && dateRange[1] && {
          from_date: dateRange[0].format('YYYY-MM-DD'),
          to_date: dateRange[1].format('YYYY-MM-DD'),
        }),
      };

      const response = await dispatch(fetchInwardPackingDateWiseReport(params)).unwrap();
      const allData = response?.data || [];

      if (allData.length === 0) {
        message.error('No data available to print.');
        setLoading(false);
        return;
      }

      // Build tableData same as your component (ensures same order)
      const pdfSource = (allData || []).map((item, idx) => ({
        key: idx + 1,
        sr_no: idx + 1,
        workOrderNo: item.workorder_no || '',
        section: item.section || '',
        date: item.date ? dayjs(item.date).format('DD-MM-YYYY') : '',
        party: item.party || '',
        length: item.length || '',
        itemName: item.item_name || '',
        packedWeight: item.packed_wt ? parseFloat(item.packed_wt).toFixed(3) : '',
        packedPcs: item.packed_pc ? String(item.packed_pc) : '',
      }));

      // Helper: compute contiguous rowSpan for a row starting at index `startIdx`
      // keysToCompare is array of keys to compare for equality (must match contiguous rows to merge)
      const getRowSpan = (arr, startIdx, keysToCompare) => {
        if (!arr[startIdx]) return 1;
        let span = 1;
        for (let i = startIdx + 1; i < arr.length; i++) {
          let same = true;
          for (const k of keysToCompare) {
            // compare empty/undefined as ''
            if ((arr[i][k] || '') !== (arr[startIdx][k] || '')) {
              same = false;
              break;
            }
          }
          if (same) span++;
          else break;
        }
        return span;
      };

      // We'll create pdfRows as shallow copy then blank out merged cells (like table rowSpan)
      const pdfRows = pdfSource.map(r => ({ ...r }));

      // We'll create a mergeMap for each dataKey -> { startIndex: span } for drawing merged cells
      const mergeMap = {
        workOrderNo: {},
        section: {},
        date: {},
        party: {},
      };

      // RULES — match HTML behavior:
      // WorkOrder: merge where contiguous workOrderNo equal -> keys ['workOrderNo']
      // Profile (section): merge only when contiguous workOrderNo + section + date are equal (profile breaks when date changes)
      // Date: merge only when contiguous workOrderNo + section + date equal (same as profile rule)
      // Customer (party): merge only when contiguous workOrderNo + section + date + party equal

      for (let i = 0; i < pdfRows.length; i++) {
        // WorkOrder
        if (!mergeMap.workOrderNo[i]) {
          const span = getRowSpan(pdfRows, i, ['workOrderNo']);
          if (span > 1) {
            mergeMap.workOrderNo[i] = span;
            for (let k = i + 1; k < i + span; k++) pdfRows[k].workOrderNo = '';
          }
        }

        // Profile (section) — merge only if same workOrderNo + section + date
        if (!mergeMap.section[i]) {
          const span = getRowSpan(pdfRows, i, ['workOrderNo', 'section', 'date']);
          if (span > 1 && (pdfRows[i].section || '') !== '') {
            mergeMap.section[i] = span;
            for (let k = i + 1; k < i + span; k++) pdfRows[k].section = '';
          }
        }

        // Date — merge only if same workOrderNo + section + date (same keys as profile)
        if (!mergeMap.date[i]) {
          const span = getRowSpan(pdfRows, i, ['workOrderNo', 'section', 'date']);
          if (span > 1 && (pdfRows[i].date || '') !== '') {
            mergeMap.date[i] = span;
            for (let k = i + 1; k < i + span; k++) pdfRows[k].date = '';
          }
        }

        // Party — merge only if same workOrderNo + section + date + party
        if (!mergeMap.party[i]) {
          const span = getRowSpan(pdfRows, i, ['workOrderNo', 'section', 'date', 'party']);
          if (span > 1 && (pdfRows[i].party || '') !== '') {
            mergeMap.party[i] = span;
            for (let k = i + 1; k < i + span; k++) pdfRows[k].party = '';
          }
        }
      }

      // Setup PDF and columns (tuned widths similar to previous version)
      const pdf = new jsPDF('p', 'pt', 'a4');
      pdf.setFont('times');
      const pageWidth = pdf.internal.pageSize.width;
      const footerHeight = 40;
      const margin = { top: 80, left: 18, right: 18, bottom: footerHeight + 40 };

      pdf.setFontSize(13);
      pdf.text('Packing Datewise Report', pageWidth / 2, 40, { align: 'center' });

      // Column definitions (only include checkedList columns, same keys as earlier)
      const allCols = [
        { header: 'Sr No.', dataKey: 'sr_no', cellWidth: 40 },
        { header: 'Work Order No.', dataKey: 'workOrderNo', cellWidth: 120 },
        { header: 'Profile', dataKey: 'section', cellWidth: 60 },
        { header: 'Date', dataKey: 'date', cellWidth: 80 },
        { header: 'Customer', dataKey: 'party', cellWidth: 120 },
        { header: 'Length (mm)', dataKey: 'length', cellWidth: 60 },
        { header: 'Profile Description', dataKey: 'itemName', cellWidth: 190 },
        { header: 'Packed Wt. (kg)', dataKey: 'packedWeight', cellWidth: 60 },
        { header: 'Packed (Pcs)', dataKey: 'packedPcs', cellWidth: 60 },
      ];
      const columns = allCols.filter(c => checkedList.includes(c.dataKey));

      // Track drawn starts to avoid redrawing
      const drawn = {
        workOrderNo: new Set(),
        section: new Set(),
        date: new Set(),
        party: new Set(),
      };

      // draw helper
      const drawMergedCell = (pdfInst, cell, span, text) => {
        const { x, y, width, height } = cell;
        const totalH = height * span;
        pdfInst.setFillColor(255, 255, 255);
        pdfInst.rect(x + 0.25, y + 0.25, width - 0.5, totalH - 0.5, 'F');
        pdfInst.setDrawColor(0, 0, 0);
        pdfInst.setLineWidth(0.6);
        pdfInst.rect(x, y, width, totalH, 'S');
        pdfInst.setFontSize(10);
        pdfInst.setFont('times', 'normal');
        pdfInst.text(String(text), x + width / 2, y + totalH / 2, { align: 'center', baseline: 'middle' });
      };

      // Generate table
      pdf.autoTable({
        columns: columns.map(c => ({ header: c.header, dataKey: c.dataKey, cellWidth: c.cellWidth })),
        body: pdfRows,
        startY: margin.top,
        theme: 'grid',
        margin,
        rowPageBreak: 'avoid',
        styles: {
          font: 'times',
          fontSize: 10,
          halign: 'center',
          overflow: 'linebreak',
          cellPadding: 4,
          lineWidth: 0.6,
        },
        headStyles: { fillColor: false, textColor: [0, 0, 0], fontStyle: 'bold' },
        columnStyles: {
          packedWeight: { halign: 'right' },
          packedPcs: { halign: 'right' },
          itemName: { halign: 'left' },
        },

        willDrawCell: (data) => {
          // hide cell text when it was blanked by merging logic
          const rowIndex = data.row.index;
          const key = data.column.dataKey;
          if ((key === 'workOrderNo' && pdfRows[rowIndex].workOrderNo === '') ||
            (key === 'section' && pdfRows[rowIndex].section === '') ||
            (key === 'date' && pdfRows[rowIndex].date === '') ||
            (key === 'party' && pdfRows[rowIndex].party === '')) {
            data.cell.text = [];
            return false;
          }
          return true;
        },

        didDrawCell: (data) => {
          const rowIndex = data.row.index;
          const key = data.column.dataKey;

          if (key === 'workOrderNo' && mergeMap.workOrderNo[rowIndex] && !drawn.workOrderNo.has(rowIndex)) {
            drawn.workOrderNo.add(rowIndex);
            drawMergedCell(pdf, data.cell, mergeMap.workOrderNo[rowIndex], mergeMap.workOrderNo[rowIndex] ? pdfSource[rowIndex].workOrderNo : '');
          }

          if (key === 'section' && mergeMap.section[rowIndex] && !drawn.section.has(rowIndex)) {
            drawn.section.add(rowIndex);
            drawMergedCell(pdf, data.cell, mergeMap.section[rowIndex], mergeMap.section[rowIndex] ? pdfSource[rowIndex].section : '');
          }

          if (key === 'date' && mergeMap.date[rowIndex] && !drawn.date.has(rowIndex)) {
            drawn.date.add(rowIndex);
            drawMergedCell(pdf, data.cell, mergeMap.date[rowIndex], mergeMap.date[rowIndex] ? pdfSource[rowIndex].date : '');
          }

          if (key === 'party' && mergeMap.party[rowIndex] && !drawn.party.has(rowIndex)) {
            drawn.party.add(rowIndex);
            drawMergedCell(pdf, data.cell, mergeMap.party[rowIndex], mergeMap.party[rowIndex] ? pdfSource[rowIndex].party : '');
          }
        },

        didDrawPage: (data) => {
          const currentPage = pdf.internal.getCurrentPageInfo().pageNumber;
          const currentDateTime = dayjs().format('DD/MM/YYYY hh:mm:ss A');
          const footerY = pdf.internal.pageSize.height - footerHeight / 2;
          pdf.setFillColor(255, 255, 255);
          pdf.rect(margin.left, footerY - 15, pageWidth - margin.left - margin.right, footerHeight, 'F');
          pdf.setFontSize(10);
          pdf.text(currentDateTime, margin.left, footerY);
          pdf.text(`Page ${currentPage}`, pageWidth - margin.right - 70, footerY);
          if (currentPage === 1) {
            pdf.addImage(logo, 'PNG', pageWidth - 100, 10, 90, 30);
          }
        },
      });

      // print
      pdf.autoPrint();
      const pdfBlob = pdf.output('blob');
      const iframe = document.createElement('iframe');
      iframe.style.position = 'fixed';
      iframe.style.right = '0px';
      iframe.style.bottom = '0px';
      iframe.style.width = '1px';
      iframe.style.height = '1px';
      iframe.style.border = 'none';
      iframe.src = URL.createObjectURL(pdfBlob);
      document.body.appendChild(iframe);
      iframe.onload = () => {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
      };

    } catch (err) {
      console.error('Print error:', err);
      message.error(`Failed to generate print: ${err?.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleExcelExport = () => {
    const headers = [
      'Sr No.',
      'Work Order No.',
      'Section',
      'Date',
      'Party',
      'Length (mm)',
      'Item Description',
      'Packed Wt. (kg)',
      'Packed (Pcs)',
    ];
    const wsData = [headers];
    const merges = [];
    let srNo = 1;
    let rowCounter = 1;
    const getRowSpan = (arr, idx, key, key2) => {
      let rowSpan = 1;
      for (let i = idx + 1; i < arr.length; i++) {
        if (key2) {
          if (arr[i][key] === arr[idx][key] && arr[i][key2] === arr[idx][key2])
            rowSpan++;
          else break;
        } else {
          if (arr[i][key] === arr[idx][key]) rowSpan++;
          else break;
        }
      }
      return rowSpan;
    };

    for (let i = 0; i < tableData.length; i++) {
      const row = tableData[i];
      let workOrderNoCell = row.workOrderNo;
      let workOrderNoRowSpan = 1;
      if (i === 0 || tableData[i - 1].workOrderNo !== row.workOrderNo) {
        workOrderNoRowSpan = getRowSpan(tableData, i, 'workOrderNo');
      } else {
        workOrderNoCell = '';
        workOrderNoRowSpan = 0;
      }
      let sectionCell = row.section;
      let sectionRowSpan = 1;
      if (
        i === 0 ||
        tableData[i - 1].workOrderNo !== row.workOrderNo ||
        tableData[i - 1].section !== row.section
      ) {
        sectionRowSpan = getRowSpan(tableData, i, 'workOrderNo', 'section');
      } else {
        sectionCell = '';
        sectionRowSpan = 0;
      }

      wsData.push([
        srNo++,
        workOrderNoCell,
        sectionCell,
        dayjs(row.date).format('DD-MM-YYYY'),
        row.party,
        row.length,
        row.itemName,
        Number(row?.packedWeight),
        Number(row?.packedPcs),
      ]);
      if (workOrderNoRowSpan > 1) {
        merges.push({
          s: { r: rowCounter, c: 1 },
          e: { r: rowCounter + workOrderNoRowSpan - 1, c: 1 },
        });
      }
      if (sectionRowSpan > 1) {
        merges.push({
          s: { r: rowCounter, c: 2 },
          e: { r: rowCounter + sectionRowSpan - 1, c: 2 },
        });
      }
      rowCounter++;
    }

    const ws = XLSX.utils.aoa_to_sheet(wsData);
    ws['!merges'] = merges;
    ws['!cols'] = [
      { wch: 8 },
      { wch: 18 },
      { wch: 12 },
      { wch: 14 },
      { wch: 18 },
      { wch: 12 },
      { wch: 18 },
      { wch: 14 },
      { wch: 14 },
    ];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Packing Datewise Report');
    XLSX.writeFile(
      wb,
      `packing_datewise_report_${dayjs().format('YYYY-MM-DD')}.xlsx`,
    );
  };

  const tableData = (PackingDateWisedata || []).map((item, idx) => ({
    key: idx + 1,
    sr_no: idx + 1,
    workOrderNo: item.workorder_no,
    section: item.section,
    date: item.date,
    party: item.party,
    length: item.length,
    itemName: item.item_name,
    packedWeight: item.packed_wt,
    packedPcs: item.packed_pc,
  }));

  const csvHeaders = [
    { label: 'Sr No.', key: 'sr_no' },
    { label: 'Work Order No.', key: 'workOrderNo' },
    { label: 'Section', key: 'section' },
    { label: 'Date', key: 'date' },
    { label: 'Party', key: 'party' },
    { label: 'Length (mm)', key: 'length' },
    { label: 'Item Name', key: 'itemName' },
    { label: 'Packed Wt. (kg)', key: 'packedWeight' },
    { label: 'Packed (Pcs)', key: 'packedPcs' },
  ];
  const csvData = tableData;

  const columns = [
    {
      title: 'Work Order No.',
      dataIndex: 'workOrderNo',
      key: 'workOrderNo',
      render: (text, row, index) => {
        const prev = tableData[index - 1];
        const next = tableData[index + 1];
        let rowSpan = 1;
        if (index > 0 && prev && prev.workOrderNo === row.workOrderNo) {
          return { children: null, props: { rowSpan: 0 } };
        }
        for (let i = index + 1; i < tableData.length; i++) {
          if (tableData[i].workOrderNo === row.workOrderNo) {
            rowSpan++;
          } else {
            break;
          }
        }
        return { children: text, props: { rowSpan } };
      },
    },
    {
      title: 'Profile',
      dataIndex: 'section',
      key: 'section',
      render: (text, row, index) => {
        const prev = tableData[index - 1];
        let rowSpan = 1;
        if (
          index > 0 &&
          prev &&
          prev.workOrderNo === row.workOrderNo &&
          prev.section === row.section
        ) {
          return { children: null, props: { rowSpan: 0 } };
        }
        for (let i = index + 1; i < tableData.length; i++) {
          if (
            tableData[i].workOrderNo === row.workOrderNo &&
            tableData[i].section === row.section
          ) {
            rowSpan++;
          } else {
            break;
          }
        }
        return { children: text, props: { rowSpan } };
      },
    },
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      render: (text, row, index) => {
        const formattedDate = dayjs(text).format('DD-MM-YYYY');
        const prev = tableData[index - 1];
        let rowSpan = 1;

        // If previous row has same WO + section + party + date → hide this row
        if (
          index > 0 &&
          prev &&
          prev.workOrderNo === row.workOrderNo &&
          prev.section === row.section &&
          prev.party === row.party &&
          dayjs(prev.date).format('DD-MM-YYYY') === formattedDate
        ) {
          return { children: null, props: { rowSpan: 0 } };
        }

        // Count next rows with same WO + section + party + date
        for (let i = index + 1; i < tableData.length; i++) {
          const nextRow = tableData[i];

          if (
            nextRow.workOrderNo === row.workOrderNo &&
            nextRow.section === row.section &&
            nextRow.party === row.party &&
            dayjs(nextRow.date).format('DD-MM-YYYY') === formattedDate
          ) {
            rowSpan++;
          } else {
            break;
          }
        }

        return { children: formattedDate, props: { rowSpan } };
      },
    },
    {
      title: 'Customer',
      dataIndex: 'party',
      key: 'party',
      render: (text, row, index) => {
        const prev = tableData[index - 1];
        let rowSpan = 1;

        // If previous row has same workOrderNo + section + party → hide this row
        if (
          index > 0 &&
          prev &&
          prev.workOrderNo === row.workOrderNo &&
          prev.section === row.section &&
          prev.party === row.party
        ) {
          return { children: null, props: { rowSpan: 0 } };
        }

        // Count how many next rows match → merge
        for (let i = index + 1; i < tableData.length; i++) {
          if (
            tableData[i].workOrderNo === row.workOrderNo &&
            tableData[i].section === row.section &&
            tableData[i].party === row.party
          ) {
            rowSpan++;
          } else {
            break;
          }
        }

        return { children: text, props: { rowSpan } };
      },
    },
    {
      title: 'Length (mm)',
      dataIndex: 'length',
      key: 'length',
    },
    {
      title: 'Profile Description',
      dataIndex: 'itemName',
      key: 'itemName',
    },
    {
      title: 'Packed Wt. (kg)',
      dataIndex: 'packedWeight',
      key: 'packedWeight',
    },
    {
      title: 'Packed (Pcs)',
      dataIndex: 'packedPcs',
      key: 'packedPcs',
    },
  ];

  const [checkedList, setCheckedList] = useState([
    'workOrderNo',
    'section',
    'date',
    'party',
    'length',
    'itemName',
    'packedWeight',
    'packedPcs',
  ]);

  const handleColumnVisibilityChange = checkedValues => {
    setCheckedList(checkedValues);
  };

  const filteredColumns = columns.filter(column =>
    checkedList.includes(column.key),
  );

  const columnMenu = (
    <Menu className="packagelistdropdown">
      <Menu.Item>
        <Checkbox.Group
          value={checkedList}
          onChange={handleColumnVisibilityChange}
        >
          <Row>
            <Col span={12}>
              <Checkbox value="workOrderNo">Work Order No.</Checkbox>
            </Col>
            <Col span={12}>
              <Checkbox value="section">Profile</Checkbox>
            </Col>
            <Col span={12}>
              <Checkbox value="date">Date</Checkbox>
            </Col>
            <Col span={12}>
              <Checkbox value="party">Customer</Checkbox>
            </Col>
            <Col span={12}>
              <Checkbox value="length">Length (mm)</Checkbox>
            </Col>
            <Col span={12}>
              <Checkbox value="itemName">Item Name</Checkbox>
            </Col>
            <Col span={12}>
              <Checkbox value="packedWeight">Packed Wt. (kg)</Checkbox>
            </Col>
            <Col span={12}>
              <Checkbox value="packedPcs">Packed (Pcs)</Checkbox>
            </Col>
          </Row>
        </Checkbox.Group>
      </Menu.Item>
    </Menu>
  );

  return (
    <div>
      <div className="sticyaction">
        <div className="sticky-header">
          <Row
            className="pagenamerow mb-0 "
            justify="space-between"
            align="middle"
          >
            <Col>
              <h2 style={{ margin: 0 }}>Packing Datewise Report</h2>
              <div className="bredcrumbwrp">
                <Link
                  className="back"
                  onClick={() => {
                    navigate(-1);
                  }}
                >
                  <Back /> BACK
                </Link>
                <Breadcrumb
                  items={[
                    { title: <Link to="/dashboard">Home</Link> },
                    { title: 'Packing Datewise Report' },
                  ]}
                />
              </div>
            </Col>
            <Col>
              <RangePicker
                value={dateRange}
                format="DD-MM-YYYY"
                onChange={handleDateRangeChange}
                style={{ width: '300px', marginRight: '10px' }}
                disabledDate={(current) => current && current > dayjs().endOf('day')}
                presets={{
                  Today: [dayjs(), dayjs()],
                  'This Week': [dayjs().startOf('week'), dayjs()],
                  'This Month': [dayjs().startOf('month'), dayjs()],
                  'Last Month': [
                    dayjs().subtract(1, 'month').startOf('month'),
                    dayjs().subtract(1, 'month').endOf('month'),
                  ],
                }}
              />
              <Space size="small">
                <Input
                  placeholder="Search"
                  className="searchbox"
                  prefix={<SearchOutlined />}
                  value={search}
                  style={{ width: 190 }}
                  onChange={handleSearch}
                />
                <Tooltip title="Filter">
                  <Button
                    type="primary"
                    disabled={!search && !orderBy}
                    className="iconlink"
                    onClick={resetFilter}
                  >
                    <FilterIcon />
                  </Button>
                </Tooltip>
                {currentUser.permissions.permissions.bundle_inward.includes(
                  'print_packing_datewise_report_copy',
                ) && (
                    <Tooltip title="Print">
                      <Button
                        type="primary"
                        className="iconlink"
                        onClick={handlePrintDownloadAll}
                      >
                        <PrintIcon />
                      </Button>
                    </Tooltip>
                  )}
                {currentUser.permissions.permissions.bundle_inward.includes(
                  'download_packing_datewise_report_excel_copy',
                ) && (
                    <Tooltip title="Export to Excel">
                      <Button
                        type="primary"
                        className="iconlink"
                        onClick={handleExcelExport}
                      >
                        <FileExcelOutlined />
                      </Button>
                    </Tooltip>
                  )}
                <div className="gridiconswrap">
                  <Tooltip placement="topLeft" title="Column Filter Options">
                    <Dropdown
                      overlay={columnMenu}
                      trigger={['click']}
                      placement="bottomRight"
                      visible={dropdownVisible}
                      onVisibleChange={setDropdownVisible}
                    >
                      <Button
                        type="primary"
                        icon={<ColumnList />}
                        onClick={e => e.preventDefault()}
                      />
                    </Dropdown>
                  </Tooltip>
                </div>
              </Space>
            </Col>
          </Row>
        </div>
      </div>
      <Spin spinning={loading} >
        <div id="printable-table">
          <Table
            columns={filteredColumns}
            dataSource={tableData}
            bordered
            pagination={false}
            style={{ marginTop: '20px' }}
          />
        </div>
      </Spin>
    </div>
  );
}

export default PackingReport;